import {useDialogContext} from 'contexts';
import {ErrorMessage, useFormikContext} from 'formik';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    InputFieldType,
    InputGroup,
    SettingContainer,
    StyledError,
    StyledInput,
} from 'shared/components/Settings/SettingsPanel';
import {Flex} from 'shared/components/StyledComponents';
import Icon from 'shared/Icon';

const EditableTextField = ({
    field,
    onSaveFieldHandler,
}: {
    field: InputFieldType;
    onSaveFieldHandler?: () => void;
}) => {
    const {showDialog} = useDialogContext();
    const inputRef = useRef<HTMLInputElement>();
    const {
        initialValues,
        handleChange,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
    } = useFormikContext<
        Partial<{
            [key: string]: string;
        }>
    >();

    const [editing, setEditing] = useState(false);

    const setEditMode = useCallback(() => {
        if (!field.confirmMessage) {
            setEditing(true);
            return;
        }

        const {message, title, yesButtonName, noButtonName} =
            field.confirmMessage;

        showDialog?.({
            title,
            className: 'confirm-modal',
            message,
            hideYesButton: true,
            hideNoButton: true,
            buttons: [
                {
                    show: true,
                    controlledHideDialog: false,
                    name: noButtonName,
                    variant: 'secondary',
                },
                {
                    show: true,
                    controlledHideDialog: false,
                    name: yesButtonName,
                    action: () => {
                        setEditing(true);
                    },
                },
            ],
        });
    }, [showDialog, field.confirmMessage]);

    const resetInitialValue = useCallback(() => {
        void setFieldValue(field.name, initialValues[field.name]);
        void setFieldTouched(field.name, false);
        setEditing(false);
    }, [initialValues]);

    const onSave = useCallback(() => {
        onSaveFieldHandler?.();
    }, [onSaveFieldHandler]);

    const onEnterKey = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                onSave();
            }
        },
        [onSave]
    );

    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);

    return (
        <InputGroup>
            <SettingContainer>
                <label>{field.label}</label>
                {!!field.infoText ? <p>{field.infoText}</p> : null}
            </SettingContainer>
            <SettingContainer style={{alignItems: 'end'}}>
                <Flex $gap="5px" $alignItems="center">
                    {editing ? (
                        <>
                            <Icon
                                key="cancel-icon"
                                style={{
                                    width: 20,
                                    height: 20,
                                    marginTop: 1,
                                    cursor: 'pointer',
                                    opacity: isSubmitting ? 0.5 : 1,
                                    pointerEvents: isSubmitting
                                        ? 'none'
                                        : 'auto',
                                }}
                                onClick={resetInitialValue}
                                iconName="Button-Error-Gray.svg"
                            />
                            <Icon
                                key="save-icon"
                                style={{
                                    width: 20,
                                    height: 20,
                                    marginTop: 1,
                                    cursor: 'pointer',
                                    opacity: isSubmitting ? 0.5 : 1,
                                    pointerEvents: isSubmitting
                                        ? 'none'
                                        : 'auto',
                                }}
                                onClick={onSave}
                                iconName="Button-Tick-Secondary.svg"
                                color="primary"
                            />
                        </>
                    ) : (
                        <Icon
                            key="edit-icon"
                            style={{
                                width: 20,
                                height: 20,
                                marginTop: 1,
                                cursor: 'pointer',
                            }}
                            onClick={setEditMode}
                            iconName="Button-Edit-Primary.svg"
                        />
                    )}
                    <StyledInput
                        innerRef={inputRef}
                        $width={field?.inputWidth}
                        aria-label={field.name}
                        name={field.name}
                        type="text"
                        autoComplete="off"
                        placeholder={field.placeholder}
                        disabled={!editing}
                        onChange={handleChange}
                        onKeyDown={onEnterKey}
                    />
                </Flex>
                <ErrorMessage component={StyledError} name={field.name} />
            </SettingContainer>
        </InputGroup>
    );
};

export default EditableTextField;
